import Fetcher from "../../fetcher";
import { getResponseData, traverseDataFields } from "./helpers";
import { once, parseObjectByType } from "../utils";
import { DEFAULT_RETRY_CONDITIONS, isJsonResponse, withSharedHeaders } from "../../fetcher/utils";
import { callback, constant, isString, isFunction, isPlainObject, noop, withProperties, getValueAtPath, resolveBoolean } from "../../utils";
import { CHECKOUT_CLOSE_EVENT } from "../../context/helpers/tokens";

const ERR_PING_RESPONSE_FAILED = Object.freeze(new Error);
const ERR_PING_ITERATIONS_EXCEEDED = Object.freeze(new Error);
const ERR_PING_HANDLER_UNAVAILABLE = Object.freeze(new Error);

const TIMEOUT_ERROR = Fetcher.TIMEOUT_ERROR;

let hasClosedCheckout = false;
let listening = false;

function startListeningForCancellation() {
  if (listening !== true) {
    addEventListener(CHECKOUT_CLOSE_EVENT, cancellationListener, false);

    listening = true;
  }
}

function stopListeningForCancellation() {
  if (listening === true) {
    removeEventListener(CHECKOUT_CLOSE_EVENT, cancellationListener, false);

    listening = false
  }
}

function cancellationListener() {
  stopListeningForCancellation();
  hasClosedCheckout = true;
}

const $fetcher = withSharedHeaders(Fetcher.one
  .limit(10)
  .retry(...DEFAULT_RETRY_CONDITIONS));

function __resolveMaxCount__(maxCount = Infinity) {
  return Number.isInteger(+maxCount)
    ? Math.max(0, Math.min(+maxCount, Infinity))
    : Infinity;
}

function __resolveDelay__(delay, defaultDelay = 1000) {
  return Number.isInteger(+delay)
    ? Math.min(10 * 1000, Math.max(0.5 * 1000, +delay))
    : +defaultDelay || 1000;
}

function __getDecayFactor__(count = 1) {
  return Number.isInteger(+count)
    ? Math.min(Math.pow(1.5, Math.floor(Math.log(Math.max(1, +count)) / Math.log(2.5))), 5)
    : 1;
}

function __pingResponseHandlerFactory__(handler, options) {
  const DEFAULT_DELAY = 1500;
  const DEFAULT_ITERATIONS = Infinity;
  const MAX_NETWORK_ERROR_RETRIES = 5;

  handler = isFunction(handler) ? handler : constant(null);

  options = parseObjectByType(options, {
    delay: ['number', DEFAULT_DELAY],
    iterations: ['number', DEFAULT_ITERATIONS],
    url: 'string'
  });

  let count = 0;
  let networkErrorCount = 0;
  let delay = __resolveDelay__(options.delay, DEFAULT_DELAY);

  const { url: pingurl } = options;
  const iterations = __resolveMaxCount__(options.iterations);

  const resetDelayOnce = once(newDelay => {
    delay = __resolveDelay__(newDelay, DEFAULT_DELAY);
  });

  return function __pingForResponseData__() {
    if (pingurl && ++count <= iterations) {
      return new Promise((resolve, reject) => {
        setTimeout(function __ping__() {
          $fetcher(pingurl)
            .then(response => {
              if (isJsonResponse(response)) {
                if (response.ok) return resolve(
                  response.json()
                    .then(handler)
                    .then(data => (resetDelayOnce(DEFAULT_DELAY), data))
                );
              }

              return reject(ERR_PING_RESPONSE_FAILED);
            })
            .catch(err => {
              // Handle network errors and timeouts with limited retries before fallback
              // Covers: Failed to fetch, NetworkError, server unreachable, DNS failures, timeouts
              const isNetworkError = err instanceof Error && 
                  (err.message.toLowerCase().includes('fetch') || 
                   err.message.toLowerCase().includes('network'));
              const isTimeout = err === TIMEOUT_ERROR;
              
              if (isNetworkError || isTimeout) {
                if (++networkErrorCount <= MAX_NETWORK_ERROR_RETRIES) {
                  --count;
                  return __pingForResponseData__().then(resolve, reject);
                }
              }
              return reject(err);
            });
        }, Math.floor(delay * __getDecayFactor__(count)));
      });
    }

    return Promise.reject(ERR_PING_ITERATIONS_EXCEEDED);
  }
}

export default function __createResponseHandlerFactoryWithPing__(delegatedHandler) {
  startListeningForCancellation();
  delegatedHandler = callback(delegatedHandler);

  return function __responseHandlerFactory__(options) {
    let {
      maxPingIterations,
      onBadResponse,
      onDataResponse,
      ...otherCallbacks
    } = isPlainObject(options) ? { ...options } : {};

    onBadResponse = callback(onBadResponse, noop /* console.log */);

    function Handler() {
      this.pingHandler = null;
    }

    Object.defineProperty(Handler.prototype, 'handle', {
      value(response) {
        if (isPlainObject(response)) {
          let data = getResponseData(response);

          if (
            (response.message === 'LONG-REQ' || 
            (response.data && 'response_parsed' in response.data && !data) || 
            (response.data && 'ping_url' in response.data)) &&
            hasClosedCheckout === false
          ) {
            if (!isFunction(this.pingHandler)) {
              this.pingHandler = __pingResponseHandlerFactory__(this.handle.bind(this), {
                iterations: +maxPingIterations,
                delay: +data.wait,
                url: data.ping_url
              });
            }

            return isFunction(this.pingHandler)
              ? this.pingHandler()
              : Promise.reject(ERR_PING_HANDLER_UNAVAILABLE);
          }

          if (!data) {
            data = Object.create(null);
          }

          let errorResponse;

          if (['error', 'failed', 'failure'].indexOf(String(response.status).toLowerCase()) >= 0) {
            errorResponse = response;
          }

          if (resolveBoolean(getValueAtPath(data, 'is_err'), !1) === true /* && isPlainObject(getValueAtPath(data, 'err_tx')) */) {
            errorResponse = data;
          }

          if (errorResponse) {
            const { error, err, message } = traverseDataFields(errorResponse, 'error', 'err', 'message');
            let $error = error || err || message;

            if ($error = ((isString($error) && $error.trim()) || null)) {
              withProperties(data, { $error }, true);
            }
          }

          // Create new charge callback context object and initialize it
          // with the `data` property (writable) containing the response data.
          const context = withProperties(Object.create(null), { data }, true);

          const options = withProperties((
            Object.keys(otherCallbacks).reduce((callbacks, key) => {
              return withProperties(callbacks, {
                [key]: callback(otherCallbacks[key])
              });
            }, Object.create(null))
          ), { onBadResponse, onDataResponse, maxPingIterations });

          onDataResponse.call(context);

          return delegatedHandler.call(withProperties(Object.create(null), { context, response, options }));
        }

        return onBadResponse(response);
      }
    });

    return function __chargeResponseHandler__(response) {
      return new Handler().handle(response);
    }
  }
}

export {
  ERR_PING_HANDLER_UNAVAILABLE,
  ERR_PING_ITERATIONS_EXCEEDED,
  ERR_PING_RESPONSE_FAILED,
  __getDecayFactor__ as getDecayFactor
};
